import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="py-20 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-2xl"></i>
        </div>
        <p className="mt-4 text-foreground-500 text-sm">文章未找到</p>
        <Link
          to="/"
          className="mt-4 inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          author: { '@type': 'Organization', name: article.author },
          image: article.image,
          inLanguage: 'zh-CN',
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin + '/' : '' },
            { '@type': 'ListItem', position: 2, name: article.categoryName, item: typeof window !== 'undefined' ? window.location.origin + '/category/' + article.category : '' },
            { '@type': 'ListItem', position: 3, name: article.title },
          ],
        }}
      />
      <article className="py-8 md:py-12 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: article.categoryName, path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mt-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {article.categoryName}
              </span>
              <div className="flex items-center gap-2 text-xs text-foreground-400">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
                <span>·</span>
                <span>{article.author}</span>
              </div>
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-500 leading-relaxed">{article.excerpt}</p>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-500 text-xs border border-background-200/60"
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div className="mt-8 w-full h-64 md:h-96 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div
            className="mt-10 prose prose-sm md:prose-base max-w-none
              prose-headings:text-foreground-950 prose-headings:font-heading prose-headings:font-semibold
              prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-foreground-700 prose-p:leading-relaxed
              prose-strong:text-foreground-900 prose-strong:font-medium
              prose-li:text-foreground-700
              [&_h2]:text-xl [&_h2]:font-heading [&_h2]:font-semibold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
              [&_h3]:text-lg [&_h3]:font-heading [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3
              [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-sm md:[&_p]:text-base
              [&_strong]:text-foreground-900 [&_strong]:font-medium
              [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4
              [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4
              [&_li]:text-foreground-700 [&_li]:mb-1
            "
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/60">
            <div className="flex items-center gap-3 text-xs text-foreground-500">
              <span>分享：</span>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer" rel="nofollow" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer" rel="nofollow" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </a>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-12 pt-8 border-t border-background-200/60">
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">相关文章</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="group p-4 rounded-lg bg-background-100 border border-background-200/60 hover:border-primary-200/50 transition-all cursor-pointer"
                  >
                    <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                      {rel.title}
                    </h4>
                    <p className="mt-2 text-xs text-foreground-500 line-clamp-2">{rel.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </>
  );
}